import React from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import core, { App, Poller } from '@gin-betting/core';
import matchList, { Pagination } from '@gin-betting/match-list';
import cd from 'debug';
import { isNil } from 'lodash';

import { Loader, NoResults } from 'components';
import { checkIfNewsPage } from 'utils';

const debug = cd('app:match-list:components:MatchList');

const { getPollerManager } = core.poller;
const { withDeferRender } = core.hocs;

const { THRESHOLD } = matchList.pagination.constants;

interface OwnProps {
    matchIdsSize: number;
    fetchStatus: App.FetchStatus;
    hasNext: boolean;
    pollerName:
        | App.PollerName.Results
        | App.PollerName.Upcoming
        | App.PollerName.Prematch
        | App.PollerName.Tournament
        | App.PollerName.Live;
    children: any;
    dropPaginationState: Pagination.DropPaginationStateFn;
    setMinAndMaxPages: Pagination.SetMinAndMaxPagesFn;
    isMedium: boolean;
    redirectToFirstPage?: boolean;
    allowEmptyMatchList?: boolean;
}

type PickedReturnedPaginationProps = Pick<
    Pagination.ReturnedPaginationProps,
    'hasMore' | 'isUp' | 'loadMoreWithPagination'
>;

type Props = OwnProps & Partial<PickedReturnedPaginationProps>;

class PaginationWrapper extends React.Component<Props> {
    poller: Poller.Poller<any> | undefined;
    scrollRef: HTMLDivElement | null;

    constructor(props: Props) {
        super(props);

        this.poller = getPollerManager().get(props.pollerName);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const { fetchStatus, matchIdsSize, redirectToFirstPage } = nextProps;

        if (
            fetchStatus === App.FetchStatus.LOADED &&
            matchIdsSize < 1 &&
            redirectToFirstPage
        ) {
            this.dropPaginationAndLoadMore();
        }
    }

    initRef = (instance: HTMLDivElement) => (this.scrollRef = instance);

    dropPaginationAndLoadMore = () => {
        this.props.dropPaginationState();
        this.loadMore();
    };

    setLoadDirectionAndLoadMore = () => {
        const { fetchStatus, matchIdsSize } = this.props;

        if (matchIdsSize === 0 || fetchStatus === App.FetchStatus.LOADING) {
            return;
        }

        if (this.props.loadMoreWithPagination) {
            this.props.loadMoreWithPagination(this.loadMore);
        } else {
            /* direction is always down in this case */
            this.props.setMinAndMaxPages();
            this.loadMore();
        }
    };

    loadMore = () => {
        if (this.poller) {
            this.poller.requestContent({
                isRepeat: false,
                isPolling: false,
                isLoadMore: true,
            });
        }
    };

    getTopGap = (): number => {
        if (!this.scrollRef) return 0;

        return window.pageYOffset + this.scrollRef.getBoundingClientRect().top;
    };

    loader = () => {
        const { fetchStatus, matchIdsSize } = this.props;

        return matchIdsSize > 15 ? (
            <Loader
                initialProgressValue={46}
                fetchStatus={fetchStatus}
                key="loader"
            />
        ) : null;
    };

    emptyListMessage = () => {
        const { fetchStatus, matchIdsSize, allowEmptyMatchList } = this.props;

        if (matchIdsSize < 1 && fetchStatus === App.FetchStatus.LOADED) {
            return allowEmptyMatchList ? null : (
                <NoResults
                    type={App.PageType.LIST}
                    linkHandler={this.dropPaginationAndLoadMore}
                />
            );
        }
    };

    render() {
        debug('render', this.props);

        const isNewsPage = checkIfNewsPage();

        const {
            hasMore: hasMorePagination,
            hasNext,
            children,
            isUp,
        } = this.props;

        if (isNewsPage) {
            return children;
        }

        // const filterMatchComponent = <AsyncFilters />;
        const hasMore = isNil(hasMorePagination) ? hasNext : hasMorePagination;
        const threshold = isUp ? this.getTopGap() + THRESHOLD : THRESHOLD;

        return (
            <div ref={this.initRef}>
                <InfiniteScroll
                    loadMore={this.setLoadDirectionAndLoadMore}
                    hasMore={isNewsPage ? false : hasMore}
                    initialLoad={false}
                    threshold={threshold}
                    isReverse={isUp}
                >
                    {children}

                    {this.emptyListMessage()}
                </InfiniteScroll>
                {!isUp && hasMore && this.loader()}
            </div>
        );
    }
}

export default withDeferRender(PaginationWrapper);
